import { useRef, useState, useEffect } from "react";
import { PrimaryButton } from "../../components/Button/Buttons";
import AIModelDropdown from "../../components/Dropdowns/AIModelDropdown";
import Input from "../../components/Inputs/Input";
import TextArea, { TextAreaWithCharacterLimit } from "../../components/Inputs/TextArea";
import { FormLabel } from "../../components/Labels/labels";
import FileUploadInSpectraEdit from "../../components/Spectra/FileUploadInSpectraEdit";
import AlertBanner from "../../components/shared/AlertBanner/AlertBanner";
import Toggle from "../../components/shared/Toggle";
import SpectraDeleteModal from "../../components/Spectra/Models/SpectraDeleteModal";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useEditSpectraMutation, useGetAIModels, useSpectraById } from "../../api/queryHooks";
import { useQueryClient } from "@tanstack/react-query";
import QUERYKEYS from "../../api/queryKeys";
import useModelPreferences from "../../hooks/useModelPreferences";
import UserAuthStore from "../../store/userStore";
import { useNavigate, useParams } from "react-router-dom";
import EmojiPickerComponent from "../../components/EmojiPicker/EmojiPicker";
import { ScreenSize } from "../../constants/const";
import ROUTES from "../../router/routes";
import Header from "../../components/Header/Header";
import useSpectraDeleteModalStore from "../../store/useSpectraDeleteModal";
import useSessionIdStore from "../../store/useSessionIdStore";
import Loader from "../../components/Loader/Loader";
import logger from "../../services/Logger";
import mediaUrlAdapter from "../../api/Media/mediaUrlAdapter";
import { isDirty } from "../../utils/isDirty";

const EditSpectraPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { space_id } = useParams();
  const { setIsOpen: setIsOpenDeleteModal } = useSpectraDeleteModalStore();
  const [files, setFiles] = useState([]);
  const [originalFiles, setOriginalFiles] = useState([]);
  // Snapshot of the loaded spectra's form values, used to gate the Update
  // button on actual changes. NOT the same as formik.dirty: initialValues in
  // the useFormik() call below is a static literal that never changes
  // reference, so enableReinitialize has no effect here — the real data is
  // loaded via individual setFieldValue calls in the effect below, which
  // would make formik.dirty read true immediately after load with zero
  // actual user edits.
  const [originalValues, setOriginalValues] = useState(null);
  const containerRef = useRef(null);
  const isFormInitialized = useRef(false);

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .trim()
      .min(1, "Title is required")
      .required("Title is required"),
    tag: Yup.string()
      .matches(/^#[a-z][a-z-]*$/, "Tag must start with # followed by at least one letter")
      .required("Tag is required"),
    aiModel: Yup.object().required("AI Model is required"),
  });

  const user = UserAuthStore((state) => state.user);
  const orgId = UserAuthStore((state) => state.orgId);
  const { data: spectraResponse, isLoading: isLoadingSpectra, isError: spectraError } = useSpectraById(space_id, orgId, user?.uid);
  const spectraData = spectraResponse?.spectra;
  const { getEnabledModels } = useModelPreferences();
  const { data: allModels, isLoading: isLoadingModels, isError: modelsError } = useGetAIModels();
  const aiModels = getEnabledModels(allModels);

  const { isPending: isLoading, mutate } = useEditSpectraMutation({
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.SPECTRA_DETAILS] });
      queryClient.invalidateQueries({ queryKey: ["spectra-by-id"] });
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.SPECTRAS] });
      navigate(ROUTES.SPECTRA.ROOT);
    },
    onError: (error) => {
      logger.error('EditSpectra', 'Edit mutation onError called', { error: error.message });
    },
  });

  const editSpectra = async (values) => {
    const { user, orgId } = UserAuthStore.getState();

    // Handle file deletions
    mutate({
      sid: space_id,
      title: values.title?.trim(),
      tag: values.tag?.trim(),
      emoji: values.emoji,
      description: values.description?.trim(),
      org_id: orgId,
      uid: user.uid,
      model_id: values.aiModel.id,
      files: files.map((file) => ({
        asset_id: file.asset_id,
        file_toc: file.file_toc ?? [],
      })),
      session_id: useSessionIdStore.getState().session_id,
      system_prompt: values.systemInstructions?.trim(),
      ...(values.webSearchEnabled !== (spectraData?.web_search_enabled !== false) && {
        web_search_enabled: Boolean(values.webSearchEnabled),
      }),
    });
  };

  const formik = useFormik({
    initialValues: {
      title: "",
      tag: "#",
      emoji: "",
      description: "",
      aiModel: null,
      systemInstructions: "",
      webSearchEnabled: true,
    },
    validationSchema,
    validateOnChange: false,
    onSubmit: editSpectra,
    enableReinitialize: true,
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    setFieldValue,
    handleBlur,
    submitCount,
  } = formik;

  // Initialize form with spectra data - simple approach
  useEffect(() => {
    if (spectraData && aiModels && aiModels.length > 0 && !isFormInitialized.current) {
      const selectedModel = aiModels.find(model => model.id === spectraData.model_id);
      
      // If model not found in aiModels, fall back to first available model
      const modelToUse = selectedModel || aiModels[0];

      const loadedValues = {
        title: spectraData.title || '',
        tag: spectraData.tag || '#',
        emoji: spectraData.emoji || '',
        description: spectraData.description || '',
        aiModel: modelToUse,
        systemInstructions: spectraData.system_prompt || '',
        webSearchEnabled: spectraData.web_search_enabled !== false,
      };
      setFieldValue('title', loadedValues.title);
      setFieldValue('tag', loadedValues.tag);
      setFieldValue('emoji', loadedValues.emoji);
      setFieldValue('description', loadedValues.description);
      setFieldValue('aiModel', loadedValues.aiModel);
      setFieldValue('systemInstructions', loadedValues.systemInstructions);
      setFieldValue('webSearchEnabled', loadedValues.webSearchEnabled);
      setOriginalValues(loadedValues);

      // Initialize files with short-lived display URLs resolved from durable IDs.
      if (spectraData.files && originalFiles.length === 0) {
        (async () => {
          const attachmentsByAssetId = new Map(
            (spectraData.attachments ?? []).map((attachment) => [attachment.asset_id, attachment])
          );
          const initialFiles = await Promise.all(
            spectraData.files.map(async (file, index) => {
              const attachment = attachmentsByAssetId.get(file.asset_id);
              let downloadURL = null;
              if (file.asset_id) {
                try {
                  downloadURL = (await mediaUrlAdapter.resolveAsset(file.asset_id)).url;
                } catch (e) {
                  logger.error('EditSpectra', 'Failed to resolve attachment', {
                    assetId: file.asset_id,
                    error: e.message,
                  });
                }
              }
              return {
                file: {
                  name: attachment?.filename,
                  size: attachment?.size_bytes,
                },
                downloadURL,
                asset_id: file.asset_id,
                file_toc: file.file_toc ?? [],
                isUploaded: true,
                progress: 100,
                id: `${file.asset_id}-${index}`,
              };
            })
          );
          setFiles(initialFiles);
          setOriginalFiles(initialFiles);
        })();
      }
      
      // Mark form as initialized
      isFormInitialized.current = true;
    }
  }, [spectraData, aiModels, setFieldValue, originalFiles.length, orgId]);

  // Custom handler for tag to transform input
  const handleTagChange = (e) => {
    let value = e.target.value
      .toLowerCase() // Convert to lowercase
      .replace(/\s+/g, '') // Remove all spaces
      .replace(/[^a-z-#]/g, ''); // Remove any characters that aren't lowercase letters, hyphens, or #

    // Ensure the value always starts with '#'
    if (value && !value.startsWith('#')) {
      value = '#' + value;
    } else if (!value) {
      value = '#';
    }

    setFieldValue('tag', value);
  };

  const hasUploadingFiles = files.some((file) => !file.isUploaded && file.progress > 0 && file.progress < 100);
  const formIsDirty = originalValues != null && (isDirty(values, originalValues) || isDirty(files, originalFiles));
  const isDisabled = isLoading || hasUploadingFiles || !formIsDirty;

  const getButtonText = () => {
    if (isLoading) return "Updating Spectra";
    if (hasUploadingFiles) return "Uploading Files...";
    return "Update Spectra";
  };

  if (isLoadingSpectra || isLoadingModels || spectraError || modelsError) {
    return (
      <div className="w-full h-screen flex items-center justify-center dark:bg-surface-page bg-surface-page">
        <Loader variant="form" />
      </div>
    );
  }

  if (!spectraData) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-ink-red-600 dark:text-ink-red-400">Spectra not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <Header type="spectra-dashboard" />
      <div className="h-[calc(100vh-80px)] overflow-auto show-scrollbar">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
          {/* Header Section */}
          <div className="bg-surface-panel dark:bg-surface-panel rounded-lg shadow-sm border border-edge-gray-200 dark:border-edge-gray-700 p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-xl font-medium text-ink-ink dark:text-ink-white">
                  Edit Spectra
                </h1>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate(`/spectra/${space_id}`)}
                  className="px-4 py-2 text-sm text-ink-gray-600 dark:text-ink-gray-300 hover:text-ink-gray-800 dark:hover:text-ink-white border border-edge-gray-300 dark:border-edge-gray-600 rounded-lg hover:bg-fill-gray-50 dark:hover:bg-fill-gray-800 transition-colors w-full sm:w-auto"
                >
                  Cancel
                </button>
                <PrimaryButton
                  type="submit"
                  form="edit-spectra-form"
                  disabled={isDisabled || isLoading}
                  className="px-6 py-2 w-full sm:w-auto"
                >
                  {getButtonText()}
                </PrimaryButton>
                <button
                  type="button"
                  onClick={() => setIsOpenDeleteModal(true, spectraData, () => navigate(ROUTES.SPECTRA.ROOT))}
                  className="px-4 py-2 text-sm text-ink-red-600 dark:text-ink-red-400 hover:text-ink-red-700 dark:hover:text-ink-red-300 border border-edge-red-300 dark:border-edge-red-600 rounded-lg hover:bg-fill-red-50 dark:hover:bg-fill-red-900/20 transition-colors w-full sm:w-auto"
                >
                  Delete Spectra
                </button>
              </div>
            </div>
          </div>

          {/* Form Section */}
          <div className="bg-surface-panel dark:bg-surface-panel rounded-lg shadow-sm border border-edge-gray-200 dark:border-edge-gray-700 p-6 mb-6">
            <form id="edit-spectra-form" onSubmit={handleSubmit}>
              <div className="space-y-6">
                    {/* Title and Emoji Row */}
                    <div className="flex flex-col sm:flex-row items-start gap-4">
                      <div className="flex-1 w-full">
                        <FormLabel htmlFor="edit-spectra-title">Title <span className="text-ink-red-500">*</span></FormLabel>
                        <Input
                          id="edit-spectra-title"
                          name="title"
                          placeholder="E.g. LinkedIn Post Generator"
                          value={values.title}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`mt-2 !border-edge-gray-200 dark:!border-edge-gray-600 ${errors.title && (touched.title || submitCount > 0) ? "!border-edge-red-500" : ""}`}
                          autoFocus
                        />
                        {errors.title && (touched.title || submitCount > 0) && (
                          <p className="text-ink-red-500 text-xs mt-1">{errors.title}</p>
                        )}
                      </div>
                      <div className="flex flex-col sm:items-center">
                        <FormLabel htmlFor="edit-spectra-emoji" className="mb-2">Emoji</FormLabel>
                        <EmojiPickerComponent
                          id="edit-spectra-emoji"
                          onEmojiSelect={(emoji) => setFieldValue("emoji", emoji)}
                          selectedEmoji={values.emoji}
                        />
                      </div>
                    </div>

                    {/* AI Model */}
                    <div>
                      <FormLabel htmlFor="edit-spectra-ai-model">AI Model <span className="text-ink-red-500">*</span></FormLabel>
                      <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400 mb-2">
                        Assign a specific model to this AI Agent
                      </p>
                      <div className="w-full sm:max-w-xs">
                        <AIModelDropdown
                          id="edit-spectra-ai-model"
                          containerRef={containerRef}
                          selectedModel={values.aiModel}
                          onSelect={(model) => setFieldValue("aiModel", model)}
                          className={`!border-edge-gray-200 dark:!border-edge-gray-600 ${errors.aiModel && (touched.aiModel || submitCount > 0) ? "!border-edge-red-500" : ""}`}
                        />
                        {errors.aiModel && (touched.aiModel || submitCount > 0) && (
                          <p className="text-ink-red-500 text-xs mt-1">AI Model is required</p>
                        )}
                      </div>
                    </div>

                      {/* Tag */}
                    <div>
                      <FormLabel htmlFor="edit-spectra-tag">Tag <span className="text-ink-red-500">*</span></FormLabel>
                      <Input
                        id="edit-spectra-tag"
                        name="tag"
                        placeholder="#tag"
                        value={values.tag}
                        onChange={handleTagChange}
                        onBlur={handleBlur}
                        className={`mt-2 w-full sm:max-w-xs !border-edge-gray-200 dark:!border-edge-gray-600 ${
                          errors.tag && (touched.tag || submitCount > 0) ? "!border-edge-red-500" : ""
                        }`}
                      />
                      {errors.tag && (touched.tag || submitCount > 0) && (
                        <p className="text-ink-red-500 text-xs mt-1">{errors.tag}</p>
                      )}
                    </div>

                    {/* Description */}
                    <div>
                      <FormLabel htmlFor="edit-spectra-description" isOptional>Description</FormLabel>
                      <TextArea
                        id="edit-spectra-description"
                        name="description"
                        className="block w-full mt-2 h-[120px] resize-none !border-edge-gray-200 dark:!border-edge-gray-600"
                        placeholder="Describe what the AI agent does for the user."
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </div>

                    {/* System Instructions */}
                    <div>
                      <FormLabel htmlFor="edit-spectra-system-instructions" isOptional>System Instructions</FormLabel>
                      <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400 mb-2">
                        Instructions to the AI that affects every prompt for this agent.
                      </p>
                      <TextAreaWithCharacterLimit
                        id="edit-spectra-system-instructions"
                        name="systemInstructions"
                        placeholder="Enter keywords..."
                        maxLength={ScreenSize}
                        value={values.systemInstructions}
                        onChange={(e) =>
                          setFieldValue("systemInstructions", e.target.value)
                        }
                        onBlur={handleBlur}
                        resizable={true}
                        minHeight="150px"
                        className="!border-edge-gray-200 dark:!border-edge-gray-600"
                      />
                    {/* Web Search Toggle */}
                    <div className="flex items-center justify-between gap-4 pt-2 border-t border-edge-gray-200 dark:border-edge-gray-700">
                      <div>
                        <FormLabel htmlFor="edit-spectra-web-search">Web Search</FormLabel>
                        <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400">
                          Allow AI in this Spectra to search the web and fetch external URLs.
                        </p>
                      </div>
                      <Toggle
                        checked={values.webSearchEnabled}
                        onChange={(checked) => setFieldValue("webSearchEnabled", checked)}
                      />
                    </div>

                    </div>

                  </div>

                  {hasUploadingFiles && (
                    <AlertBanner variant="info" className="mt-6">
                      Please wait for file uploads to complete before updating the Spectra.
                    </AlertBanner>
                  )}
                </form>
              </div>

          {/* File Attachments Section */}
          <div className="bg-surface-panel dark:bg-surface-panel rounded-lg shadow-sm border border-edge-gray-200 dark:border-edge-gray-700 p-6">
            <FileUploadInSpectraEdit
              files={files}
              setFiles={setFiles}
              spaceId={space_id}
            />
          </div>
        </div>
      </div>

      <SpectraDeleteModal />
    </div>
  );
};

export default EditSpectraPage;
